"use client";

import { Slot } from "@/components/ui/Slot";
import { clsx } from "@frend-digital/ui";
import { atom, Provider, useAtom } from "jotai";
import styles from "../ProductCard.module.css";
import type { PropsWithChildren } from "react";
import { HydrateAtoms } from "@/components/ui/HydrateAtoms";
import Image from "next/image";
import { getProductLink } from "../util";
import type { ProductCard } from "@/lib/centra";
import { Link } from "@/i18n/Link";

export const variantAtom = atom<string | null>(null);

export const ProductVariantTrigger = ({
	children,
	variant,
}: {
	children: React.ReactNode;
	variant: string;
}) => {
	const [currentVariant, setVariant] = useAtom(variantAtom);
	return (
		<Slot
			className={clsx(currentVariant === variant && styles.active)}
			onMouseEnter={() => {
				setVariant(variant);
			}}
		>
			{children}
		</Slot>
	);
};

export const ProductProvider = ({
	children,
	defaultVariant,
}: PropsWithChildren<{ defaultVariant: string }>) => {
	return (
		<Provider>
			<HydrateAtoms values={[[variantAtom, defaultVariant]]} />
			{children}
		</Provider>
	);
};

export const VariantDot = ({
	variant: v,
	parentHref,
}: { variant: ProductCard["variants"][number]; parentHref?: string }) => {
	const hasUrl = !!v.uri;
	const link = (
		<Link
			className={clsx(styles.variantDot, v.baseColor && styles.splitVariant)}
			style={
				{
					"--variant-color": v.display_color || v.color?.hex,
					"--variant-color-secondary": v.baseColor,
				} as React.CSSProperties
			}
			href={getProductLink(parentHref, v)}
			aria-label={v.variantName}
		/>
	);

	const imageUrl = v.media?.[0]?.href;

	if (!hasUrl && !imageUrl) return null;

	const image = (
		<Image
			className={clsx(styles.variantDot)}
			width={50}
			height={50}
			src={imageUrl}
			alt={v.variantName}
		/>
	);

	return (
		<ProductVariantTrigger variant={v.displayItemId}>{hasUrl ? link : image}</ProductVariantTrigger>
	);
};
